import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Layers, List } from 'lucide-react';
import type { DimensionValueGroupingToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useDimensionGroupingsList, useDimensionGroupingMutations, useDimensionGroupingTypes } from '~/hooks/use-dimension-groupings';
import { tokenToForm, type DimensionGroupingFormValues } from '~/lib/schemas/dimension-grouping';
import { DimensionGroupingEditDialog } from './grouping-edit-dialog';
import { GroupingValuesSheet } from './grouping-values-sheet';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Dimension value groupings (Ομαδοποίηση Διαστάσεων) CRUD surface + read-only values view. */
export function DimensionGroupingsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useDimensionGroupingsList();
	const { data: types } = useDimensionGroupingTypes();
	const { remove } = useDimensionGroupingMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<DimensionGroupingFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<DimensionValueGroupingToken | null>(null);
	const [valuesFor, setValuesFor] = useState<DimensionValueGroupingToken | null>(null);

	const typeName = useMemo(() => {
		const map = new Map<number, string>();
		for (const ty of types ?? []) if (ty.id != null) map.set(ty.id, ty.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? '') : '');
	}, [types]);

	const columns = useMemo<SystemGridColumn<DimensionValueGroupingToken>[]>(
		() => [
			{
				key: 'name',
				header: t('attributes.dimensionGroupings.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('attributes.dimensionGroupings.columns.code'),
				width: '180px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'type',
				header: t('attributes.dimensionGroupings.columns.type'),
				width: '170px',
				sortValue: o => typeName(o.dgt as number | undefined),
				exportValue: o => typeName(o.dgt as number | undefined),
				render: o => <span className="truncate text-sm text-foreground-muted">{typeName(o.dgt as number | undefined) || '—'}</span>
			},
			{
				key: 'values',
				header: '',
				width: '130px',
				render: o => (
					<Button
						variant="outline"
						size="sm"
						onClick={e => {
							e.stopPropagation();
							setValuesFor(o);
						}}>
						<List size={14} /> {t('attributes.dimensionGroupings.values')}
					</Button>
				)
			},
			{
				key: 'visible',
				header: t('attributes.dimensionGroupings.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('attributes.dimensionGroupings.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t, typeName]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<DimensionValueGroupingToken>
				gridId="dimension-groupings"
				title={t('attributes.dimensionGroupings.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Layers size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('attributes.dimensionGroupings.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="dimension-groupings"
			/>

			<DimensionGroupingEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<GroupingValuesSheet
				open={Boolean(valuesFor)}
				onOpenChange={o => !o && setValuesFor(null)}
				groupingId={valuesFor?.id ?? null}
				groupingName={valuesFor?.nm ?? ''}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('attributes.dimensionGroupings.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('attributes.dimensionGroupings.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
